import { useFormContext, useWatch } from "react-hook-form";
import { isEmailChannel } from "../../channels/types";
import { useBuiltHtmlMessage } from "../component/buttons/preview-button/preview-dialog/hooks/use-built-html-message";
import { useBuiltMjmlMessage } from "../component/buttons/preview-button/preview-dialog/hooks/use-built-mjml-message";
import { getEmailPreviewSource } from "../component/buttons/preview-button/preview-dialog/hooks/use-email-preview-source";
import type { MomentFormMoment } from "../types/moment-form-moment";

const NO_PREVIEW: { loading: false; data: null; error: null } = {
  loading: false,
  data: null,
  error: null,
};

export function useShowroomData(): {
  data: { subject: string; body: string } | null;
  loading: boolean;
} {
  const { getValues, control } = useFormContext<MomentFormMoment>();
  useWatch({
    control,
    name: ["channel", "use_lexical_editor", "email_subject", "email_moment", "design", "from"],
  });

  const [channel, use_lexical_editor, email_subject, email_moment, design, from] = getValues([
    "channel",
    "use_lexical_editor",
    "email_subject",
    "email_moment",
    "design",
    "from",
  ]);

  const [email_design_id, account_id, poll_ids] = getValues([
    "email_design_id",
    "account_id",
    "poll_ids",
  ]);

  const {
    design: preview_design,
    subject,
    additional_merge_fields: email_additional_merge_fields,
    use_unlayer_email_preview,
  } = getEmailPreviewSource({
    channel,
    use_lexical_editor: use_lexical_editor ?? false,
    design,
    email_subject,
    from,
    email_moment,
  });

  const htmlMessage = useBuiltHtmlMessage({
    body: email_moment?.body ?? undefined,
    dynamic_segment_id_map: null,
    preview_info: NO_PREVIEW,
    preview_test_data: NO_PREVIEW,
    design: preview_design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
    dynamic_condition_fields: {},
  });

  const mjmlMessage = useBuiltMjmlMessage({
    email_design_id,
    account_id,
    poll_ids,
    form_design: design,
    dynamic_segment_id_map: null,
    preview_info: NO_PREVIEW,
    preview_test_data: NO_PREVIEW,
    preview_design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
    dynamic_condition_fields: {},
  });

  if (!isEmailChannel(channel)) {
    return { data: null, loading: false };
  }

  const result = use_lexical_editor ? mjmlMessage : htmlMessage;
  return {
    data: result.data ? { subject: subject ?? "", body: result.data } : null,
    loading: result.loading,
  };
}
